import * as React from 'react';
import { Plus, Undo2 } from 'lucide-react';
import { useMutation, useQueryClient } from 'react-query';
import { cn } from 'lib/utils';
import { notify } from 'services';
import type { ServiceOrder } from 'contracts/service-orders';
import { useAuth } from 'hooks';
import { useCreateTask } from 'hooks/api/service-orders/use-create-task';
import { useCreateObservation } from 'hooks/api/service-orders/use-create-observation';
import { useCancelServiceOrder } from 'hooks/api/service-orders';
import { Box } from 'components/ui/Box';
import { Skeleton } from 'components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Button } from 'components/ui/button';
import {
  CreateObservationDrawer,
  type CreateObservationDrawerRef,
} from './create-observation-drawer';
import {
  CreateTaskDrawer,
  type CreateTaskDrawerRef,
} from './create-task-drawer';
import {
  CancelOrderDrawer,
  type CancelOrderDrawerRef,
} from './cancel-order-drawer';
import { generateServiceOrderPDF } from './print-service-order';

type Props = React.HTMLAttributes<HTMLDivElement> & {
  serviceOrder?: ServiceOrder;
  loading?: boolean;
  onClickGoBack?: () => Promise<void> | void;
  enableActionsDropdown?: boolean;
};

export const Header = ({ serviceOrder, loading, onClickGoBack, enableActionsDropdown, className }: Props) => {
  const createTaskDrawerRef = React.useRef<CreateTaskDrawerRef>(null);
  const createObservationDrawerRef =
    React.useRef<CreateObservationDrawerRef>(null);
  const cancelOrderDrawerRef = React.useRef<CancelOrderDrawerRef>(null);

  const { profile } = useAuth();
  const { createTask } = useCreateTask();
  const { createObservation } = useCreateObservation();
  const { cancelOrder } = useCancelServiceOrder();
  const queryClient = useQueryClient();

  const isAdmin = React.useMemo(() => profile?.type === 'admin', [profile?.type]);

  const products = React.useMemo(() => {
    if (!serviceOrder) return [];
    return serviceOrder.canceledAt
      ? serviceOrder.canceledProducts
      : serviceOrder.products;
  }, [serviceOrder]);

  const createTaskMutation = useMutation({
    mutationFn: createTask,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['fetch-service-order']);
      notify('success', 'Trabalho adicionado com sucesso!');
    },
    onError: () => {
      notify('error', 'Erro ao adicionar trabalho.');
    },
    onSettled: () => {
      createTaskDrawerRef.current?.close();
    },
  });

  const createObservationMutation = useMutation({
    mutationFn: createObservation,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['fetch-service-order']);
      notify('success', 'Comentário adicionado com sucesso!');
    },
    onError: () => {
      notify('error', 'Erro ao adicionar comentário.');
    },
    onSettled: () => {
      createObservationDrawerRef.current?.close();
    },
  });

  const cancelOrderMutation = useMutation({
    mutationKey: ['cancel-service-order'],
    mutationFn: cancelOrder,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['fetch-service-order']);
      notify('success', 'Ordem de serviço cancelada com sucesso!');
    },
    onError: (error: any) => {
      notify('error', error?.message);
    },
    onSettled: () => {
      cancelOrderDrawerRef.current?.close();
    },
  });

  const onTaskSubmit = React.useCallback(
    (formData: { task: string }) => {
      if (!serviceOrder) return;

      createTaskMutation.mutate({
        serviceOrderId: serviceOrder.id,
        task: formData.task,
      });
    },
    [createTaskMutation, serviceOrder],
  );

  const onObservationSubmit = React.useCallback(
    (formData: { observation: string }) => {
      if (!serviceOrder) return;

      createObservationMutation.mutate({
        serviceOrderId: serviceOrder.id,
        observation: formData.observation,
      });
    },
    [createObservationMutation, serviceOrder],
  );

  if (!serviceOrder || loading) {
    return (
      <div className={cn('flex gap-6', className)}>
        <HeaderSkeleton />
      </div>
    );
  }

  return (
    <Box className="py-4 flex items-center justify-between">
      <h1 className="text-sm font-bold text-gray-600">
        Ordem de serviço: {serviceOrder.id}
      </h1>
      <div className="flex gap-2">
        <CreateObservationDrawer
          ref={createObservationDrawerRef}
          onSubmit={onObservationSubmit}
          loading={createObservationMutation.isLoading}
        />
        <CreateTaskDrawer
          ref={createTaskDrawerRef}
          onSubmit={onTaskSubmit}
          loading={createTaskMutation.isLoading}
        />
        <CancelOrderDrawer
          ref={cancelOrderDrawerRef}
          onSubmit={cancelOrderMutation.mutate}
          loading={cancelOrderMutation.isLoading}
        />
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="border-t gap-2" disabled={!enableActionsDropdown}>
              <Plus className="w-4 h-4 text-slate-600" />
              Ações
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className={'max-w-max'} align={'start'}>
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => createObservationDrawerRef.current?.open()}>
                Novo comentário
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!!serviceOrder.canceledAt}
                onClick={() => createTaskDrawerRef.current?.open()}
              >
                Novo trabalho
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem
                  disabled={!!serviceOrder.canceledAt}
                  onClick={() => cancelOrderDrawerRef.current?.open(serviceOrder.id)}
                >
                  Cancelar ordem de serviço
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => generateServiceOrderPDF(serviceOrder.id, products, serviceOrder)}>
                Imprimir
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="ghost" className="gap-2" onClick={onClickGoBack}>
          <Undo2 className="w-4 h-4 text-slate-600" />
          Voltar
        </Button>
      </div>

    </Box>
  );
};

const HeaderSkeleton = () => {
  return <Skeleton className="w-full h-[72px]" />;
};
